import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight, FileDown, MessageCircle } from "lucide-react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";

const PaymentSuccess = () => {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("polling"); // polling | paid | expired | error
    const [amount, setAmount] = useState(0);
    const [currency, setCurrency] = useState("SAR");
    const [reservationId, setReservationId] = useState(null);
    const { settings, formatPrice } = useApp();

    useEffect(() => {
        if (!sessionId) { setStatus("error"); return; }
        let attempts = 0;
        const maxAttempts = 10;
        const poll = async () => {
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setStatus("paid");
                    setAmount(data.amount || 0);
                    setCurrency((data.currency || "sar").toUpperCase());
                    setReservationId(data.reservation_id);
                    return;
                }
                if (data.status === "expired") { setStatus("expired"); return; }
                if (attempts++ >= maxAttempts) { setStatus("error"); return; }
                setTimeout(poll, 2000);
            } catch { setStatus("error"); }
        };
        poll();
    }, [sessionId]);

    const wa = (settings.whatsapp_number || "").replace(/\D/g, "");
    const waHref = wa ? `https://wa.me/${wa}?text=${encodeURIComponent("Merhaba, Stripe ile ödeme yaptım. Rezervasyonumu kontrol edebilir misiniz?")}` : null;

    return (
        <section className="min-h-[70vh] flex items-center justify-center p-6" data-testid="payment-success-page">
            <div className="max-w-lg w-full bg-emerald_surface border border-border_green p-8 text-center">
                {status === "polling" && (
                    <>
                        <Loader2 className="w-14 h-14 mx-auto text-gold animate-spin mb-4" />
                        <h1 className="font-serif text-3xl mb-2">Ödemeniz Kontrol Ediliyor</h1>
                        <p className="text-muted_green text-sm">Lütfen bekleyin, ödeme durumunuz Stripe'tan alınıyor.</p>
                    </>
                )}
                {status === "paid" && (
                    <>
                        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-600/30 border border-emerald-500 flex items-center justify-center mb-4">
                            <CheckCircle2 className="w-8 h-8 text-emerald-300" />
                        </div>
                        <h1 className="font-serif text-3xl text-gold mb-2">Ödeme Başarılı 🎉</h1>
                        <p className="text-bone/80 leading-relaxed mb-4">
                            {amount > 0 && (<span className="block font-mono text-lg text-gold">{formatPrice ? formatPrice(amount) : `${amount} ${currency}`}</span>)}
                            tutarındaki ödemeniz alındı. Rezervasyonunuz onay için ekibimize iletildi. Kısa süre içinde WhatsApp üzerinden dönüş yapılacaktır.
                        </p>
                        <div className="flex items-center justify-center gap-3 flex-wrap mt-6">
                            <Link to="/profile" className="btn-gold" data-testid="go-to-profile"><FileDown className="w-4 h-4" /> Hesabıma Git</Link>
                            {waHref && <a href={waHref} target="_blank" rel="noreferrer" className="btn-outline-gold"><MessageCircle className="w-4 h-4" /> WhatsApp</a>}
                        </div>
                    </>
                )}
                {status === "expired" && (
                    <>
                        <XCircle className="w-14 h-14 mx-auto text-rose-400 mb-4" />
                        <h1 className="font-serif text-3xl mb-2">Ödeme Süresi Doldu</h1>
                        <p className="text-muted_green text-sm mb-4">Ödeme oturumunuz zaman aşımına uğradı. Lütfen tekrar deneyin.</p>
                        {reservationId && <Link to={`/track`} className="btn-outline-gold">Rezervasyonu Sorgula <ArrowRight className="w-4 h-4" /></Link>}
                    </>
                )}
                {status === "error" && (
                    <>
                        <XCircle className="w-14 h-14 mx-auto text-rose-400 mb-4" />
                        <h1 className="font-serif text-3xl mb-2">Bir Sorun Oluştu</h1>
                        <p className="text-muted_green text-sm mb-4">Ödeme durumunuzu kontrol ederken hata oluştu. Lütfen bizimle iletişime geçin.</p>
                        {waHref && <a href={waHref} target="_blank" rel="noreferrer" className="btn-gold"><MessageCircle className="w-4 h-4" /> WhatsApp Destek</a>}
                    </>
                )}
            </div>
        </section>
    );
};

export default PaymentSuccess;
